/**
 * Inspection Risk Assessment - in-app form or PDF upload.
 *
 * Replaces the paper "Updated Risk Assessment Form 040221_Fillable.pdf"
 * inspectors fill before going on the roof. Per Juan's direction we
 * offer BOTH: younger FTSs use the in-app form on their phone; veteran
 * FTSs upload the completed paper PDF.
 *
 * This modal is the CAPTURE STEP of the `begin_risk_assessment` workflow
 * action - it does not exist in isolation. When opened from that action
 * (driveActionKey set) it PATCHes `Inspection.risk_assessment` and then
 * drives the engine transition (confirmed -> risk_assessment) in one flow,
 * so completing the form is what begins the risk assessment. Opened plainly
 * from the Risk Assessment tab (no driveActionKey) it just edits the saved
 * answers in place.
 *
 * Three sections of the in-app form:
 *   1. Roof context (type, stories, pitch, access method) - grid form
 *   2. Inspector wellness (5 questions; any YES blocks proceed)
 *   3. 20 safety checklist items (yes/no/N/A), grouped general vs ladder
 *
 * Steep-pitch gate: if pitch > 7/12 the form forces a fall-protection
 * narrative before allowing submit (mirrors the PDF's policy).
 */

import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Separator } from '@/shared/components/ui/separator'
import { FileUploader } from '@/shared/components/ui/file-uploader'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import { CheckCircle2, AlertTriangle, Loader2, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'
import { toastApiError } from '@/shared/lib/api-error'
import { useUpdateInspection } from '@/admin/hooks/use-inspections'
import { useUploadFile } from '@/admin/hooks/use-products'
import { useExecuteWorkflowAction } from '@/shared/hooks/use-workflows'
import {
  SAFETY_GROUPS,
  SAFETY_QUESTIONS,
  WELLNESS,
  type AnswerValue,
} from './inspection-risk-assessment-data'

interface RiskAssessmentData {
  mode: 'in_app' | 'upload'
  // In-app fields
  roof_type?: 'hip' | 'gable' | 'other'
  number_of_stories?: number
  pitch?: string
  access_method?: 'ladder' | 'mewp'
  wellness: Record<string, boolean>
  answers: Record<string, AnswerValue>
  fall_protection_plan?: string
  signed_by: string
  // Upload fields
  uploaded_file_url?: string
}

// ---------------------------------------------------------------------------
// SegmentedToggle - the shared "switch control" for the checklist rows.
//
// Built from the shared Button (no raw <button>). Tone-aware so the same
// control serves both the boolean wellness rows (No = good/green,
// Yes = bad/red) and the tri-state safety rows (Yes = good, No = bad,
// N/A = neutral). The selected segment fills with its semantic colour.
// ---------------------------------------------------------------------------

type SegTone = 'good' | 'bad' | 'neutral'

// Each tone re-asserts its colour on `hover:` too. The shared ghost Button
// carries `hover:bg-muted hover:text-foreground`; without a matching
// `hover:` here, tailwind-merge cannot dedupe the ghost hover against our
// resting `bg-*`/`text-*`, so hovering an active segment greyed it out.
const SEG_ACTIVE: Record<SegTone, string> = {
  good: 'bg-[var(--status-ok)] text-white hover:bg-[var(--status-ok)] hover:text-white',
  bad: 'bg-[var(--status-error)] text-white hover:bg-[var(--status-error)] hover:text-white',
  neutral:
    'bg-[var(--ink-3)] text-white hover:bg-[var(--ink-3)] hover:text-white',
}

interface SegOption<T extends string> {
  key: T
  label: string
  tone: SegTone
}

function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  disabled,
  ariaLabel,
}: {
  options: SegOption<T>[]
  value: T | null
  onChange: (next: T) => void
  disabled?: boolean
  ariaLabel?: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex shrink-0 items-center gap-1 rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-1"
    >
      {options.map((o) => {
        const active = value === o.key
        return (
          <Button
            key={o.key}
            type="button"
            variant="ghost"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(o.key)}
            className={cn(
              'h-9 min-w-[48px] rounded-full px-3 text-[12px] font-normal shadow-none',
              active
                ? cn('font-medium hover:opacity-90', SEG_ACTIVE[o.tone])
                : 'bg-transparent text-[var(--ink-3)] hover:bg-[var(--bg-card)] hover:text-[var(--ink-2)]',
            )}
          >
            {o.label}
          </Button>
        )
      })}
    </div>
  )
}

const WELLNESS_OPTIONS: SegOption<'no' | 'yes'>[] = [
  { key: 'no', label: 'No', tone: 'good' },
  { key: 'yes', label: 'Yes', tone: 'bad' },
]

const SAFETY_OPTIONS: SegOption<'yes' | 'no' | 'na'>[] = [
  { key: 'yes', label: 'Yes', tone: 'good' },
  { key: 'no', label: 'No', tone: 'bad' },
  { key: 'na', label: 'N/A', tone: 'neutral' },
]

interface RiskAssessmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  inspectionUuid: string
  initialAssessment?: Record<string, unknown>
  /**
   * When provided together with {@link driveActionKey}, completing the form
   * ALSO drives that workflow action on this instance after the PATCH, so the
   * modal is the capture step of `begin_risk_assessment` rather than a
   * standalone edit. Omit both for plain edit-in-place from the RA tab.
   */
  workflowInstanceUuid?: string | null
  driveActionKey?: string | null
  /** Called after a successful save (and transition, if driven). */
  onCompleted?: () => void
}

export function RiskAssessmentDialog({
  open,
  onOpenChange,
  inspectionUuid,
  initialAssessment,
  workflowInstanceUuid,
  driveActionKey,
  onCompleted,
}: RiskAssessmentDialogProps) {
  const { t } = useTranslation()
  const update = useUpdateInspection(inspectionUuid)
  const uploadFile = useUploadFile()
  const execute = useExecuteWorkflowAction(workflowInstanceUuid ?? '')
  const [mode, setMode] = useState<'in_app' | 'upload'>('in_app')
  const [data, setData] = useState<RiskAssessmentData>(() => ({
    mode: (initialAssessment?.mode as 'in_app' | 'upload') ?? 'in_app',
    roof_type: initialAssessment?.roof_type as 'hip' | 'gable' | 'other' | undefined,
    number_of_stories: initialAssessment?.number_of_stories as number | undefined,
    pitch: initialAssessment?.pitch as string | undefined,
    access_method: initialAssessment?.access_method as 'ladder' | 'mewp' | undefined,
    wellness: (initialAssessment?.wellness as Record<string, boolean>) ?? {},
    answers: (initialAssessment?.answers as Record<string, AnswerValue>) ?? {},
    fall_protection_plan: initialAssessment?.fall_protection_plan as string | undefined,
    signed_by: (initialAssessment?.signed_by as string) ?? '',
    uploaded_file_url: initialAssessment?.uploaded_file_url as string | undefined,
  }))

  const isBeginFlow = Boolean(workflowInstanceUuid && driveActionKey)
  const busy = update.isPending || execute.isPending

  const pitchNum = useMemo(() => {
    if (!data.pitch) return 0
    const top = data.pitch.split('/')[0]
    return parseInt(top, 10) || 0
  }, [data.pitch])
  const isSteep = pitchNum > 7

  const wellnessBlocked = useMemo(
    () => Object.values(data.wellness).some((v) => v === true),
    [data.wellness],
  )

  const completionStats = useMemo(() => {
    const total = SAFETY_QUESTIONS.length
    const answered = SAFETY_QUESTIONS.filter(
      (q) => data.answers[String(q.id)] && data.answers[String(q.id)] !== '',
    ).length
    const yesCount = SAFETY_QUESTIONS.filter(
      (q) => data.answers[String(q.id)] === 'yes',
    ).length
    return { total, answered, yesCount }
  }, [data.answers])

  const canSubmit =
    mode === 'upload'
      ? !!data.uploaded_file_url && !!data.signed_by
      : !wellnessBlocked &&
        completionStats.answered === completionStats.total &&
        !!data.roof_type &&
        !!data.access_method &&
        !!data.signed_by &&
        (!isSteep || !!data.fall_protection_plan)

  const handleSubmit = useCallback(async () => {
    try {
      // Send completed_at both nested (so the saved JSON carries its
      // own timestamp for posterity) and at the top level (so the
      // Inspection.risk_assessment_completed_at column gets populated -
      // the detail page reads that column to flip the button from
      // "Begin" → "Edit" and to surface the saved-answers summary).
      const completedAt = new Date().toISOString()
      await update.mutateAsync({
        risk_assessment: { ...data, completed_at: completedAt },
        risk_assessment_completed_at: completedAt,
        risk_assessment_signed_by: data.signed_by,
      } as never)
      // When opened as the capture step of a workflow action, drive the
      // engine transition now (confirmed -> risk_assessment) so filling
      // the form is what begins the risk assessment.
      if (isBeginFlow && workflowInstanceUuid && driveActionKey) {
        await execute.mutateAsync({ action_key: driveActionKey })
      }
      toast.success(
        isBeginFlow
          ? 'Risk assessment recorded. Inspection moved to Risk Assessment.'
          : 'Risk assessment recorded.',
      )
      onCompleted?.()
      onOpenChange(false)
    } catch (e) {
      toastApiError(e, 'Failed to save risk assessment.')
    }
  }, [
    data,
    driveActionKey,
    execute,
    isBeginFlow,
    onCompleted,
    onOpenChange,
    update,
    workflowInstanceUuid,
  ])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Roof Pre-Inspection Risk Assessment</DialogTitle>
          <DialogDescription>
            {isBeginFlow
              ? t(
                  'inspections.riskAssessment.beginDescription',
                  'Complete this safety review to begin the risk assessment. The inspection cannot proceed until it is done.',
                )
              : t(
                  'inspections.riskAssessment.description',
                  'Before going on the roof, complete the safety review. Replaces the paper form. Veteran FTSs may upload a signed PDF instead.',
                )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v) => setMode(v as 'in_app' | 'upload')}>
          <TabsList variant="pill" className="w-fit">
            <TabsTrigger value="in_app">In-app form</TabsTrigger>
            <TabsTrigger value="upload">Upload completed PDF</TabsTrigger>
          </TabsList>

          {/* ── In-app form ─────────────────────────────────────── */}
          <TabsContent value="in_app" className="flex flex-col gap-6 pt-4">
            {/* Roof context - grid form at the top */}
            <section className="flex flex-col gap-4">
              <h3 className="text-xs uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Roof context
              </h3>
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label>Roof type *</Label>
                  {/* Transitional: roof type options should be driven by
                      sub-type configuration_data or a choices endpoint. */}
                  <Select
                    value={data.roof_type ?? ''}
                    onValueChange={(v) =>
                      setData((d) => ({ ...d, roof_type: v as 'hip' | 'gable' | 'other' }))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hip">Hip</SelectItem>
                      <SelectItem value="gable">Gable</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Number of stories</Label>
                  <Input
                    type="number"
                    min={1}
                    max={5}
                    value={data.number_of_stories ?? ''}
                    onChange={(e) =>
                      setData((d) => ({
                        ...d,
                        number_of_stories: e.target.value
                          ? parseInt(e.target.value, 10)
                          : undefined,
                      }))
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>
                    Pitch <span className="text-[var(--ink-3)]">(e.g. 5/12)</span>
                  </Label>
                  <Input
                    value={data.pitch ?? ''}
                    onChange={(e) =>
                      setData((d) => ({ ...d, pitch: e.target.value }))
                    }
                    placeholder="5/12"
                  />
                  {isSteep && (
                    <Badge variant="destructive" className="w-fit">
                      <AlertTriangle size={12} className="mr-1" />
                      Steep slope ({data.pitch}) - fall protection required
                    </Badge>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Access method *</Label>
                  {/* Transitional: access method options should be driven by
                      sub-type configuration_data or a choices endpoint. */}
                  <Select
                    value={data.access_method ?? ''}
                    onValueChange={(v) =>
                      setData((d) => ({ ...d, access_method: v as 'ladder' | 'mewp' }))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ladder">Ladder</SelectItem>
                      <SelectItem value="mewp">Lift (MEWP)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <Separator />

            {/* Wellness */}
            <section className="flex flex-col gap-3">
              <h3 className="text-xs uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Inspector wellness - any YES blocks the inspection
              </h3>
              <div className="overflow-hidden rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] divide-y divide-[var(--rule)]">
                {WELLNESS.map((q) => {
                  const v =
                    data.wellness[q.key] === true
                      ? 'yes'
                      : data.wellness[q.key] === false
                        ? 'no'
                        : null
                  return (
                    <div
                      key={q.key}
                      className="grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-3"
                    >
                      <span className="text-[13px] leading-snug text-[var(--ink-2)]">
                        {q.text}
                      </span>
                      <SegmentedToggle
                        ariaLabel={q.text}
                        options={WELLNESS_OPTIONS}
                        value={v}
                        disabled={busy}
                        onChange={(next) =>
                          setData((d) => ({
                            ...d,
                            wellness: { ...d.wellness, [q.key]: next === 'yes' },
                          }))
                        }
                      />
                    </div>
                  )
                })}
              </div>
              {wellnessBlocked && (
                <Badge variant="destructive" className="w-fit">
                  <AlertTriangle size={12} className="mr-1" />
                  Inspection blocked - resolve wellness flags before proceeding
                </Badge>
              )}
            </section>

            <Separator />

            {/* Safety checklist - grouped tables */}
            <section className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  Safety checklist
                </h3>
                <span className="text-[11px] text-[var(--ink-3)]">
                  {completionStats.answered}/{completionStats.total} answered
                </span>
              </div>
              {SAFETY_GROUPS.map((group) => {
                const items = SAFETY_QUESTIONS.filter((q) => q.group === group.key)
                if (items.length === 0) return null
                const answeredInGroup = items.filter(
                  (q) => data.answers[String(q.id)] && data.answers[String(q.id)] !== '',
                ).length
                return (
                  <div
                    key={group.key}
                    className="overflow-hidden rounded-lg border border-[var(--rule)] bg-[var(--bg-card)]"
                  >
                    <div className="flex items-center justify-between border-b border-[var(--rule)] bg-[var(--bg-light)] px-4 py-2.5">
                      <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)]">
                        {group.label}
                      </span>
                      <span className="text-[11px] text-[var(--ink-3)]">
                        {answeredInGroup}/{items.length}
                      </span>
                    </div>
                    <div className="divide-y divide-[var(--rule)]">
                      {items.map((q) => {
                        const v = (data.answers[String(q.id)] || null) as
                          | 'yes'
                          | 'no'
                          | 'na'
                          | null
                        return (
                          <div
                            key={q.id}
                            className="grid grid-cols-[1.75rem_1fr_auto] items-start gap-3 px-4 py-3"
                          >
                            <span className="pt-2 font-mono text-[11px] text-[var(--ink-3)]">
                              {q.id}
                            </span>
                            <span className="pt-1.5 text-[13px] leading-snug text-[var(--ink-2)]">
                              {q.text}
                            </span>
                            <SegmentedToggle
                              ariaLabel={q.text}
                              options={SAFETY_OPTIONS}
                              value={v}
                              disabled={busy}
                              onChange={(next) =>
                                setData((d) => ({
                                  ...d,
                                  answers: { ...d.answers, [String(q.id)]: next },
                                }))
                              }
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </section>

            {/* Steep-pitch narrative gate */}
            {isSteep && (
              <section className="flex flex-col gap-1.5 rounded-lg border border-[var(--status-error)] bg-[var(--bg-light)] p-4">
                <Label htmlFor="fall_protection_plan">
                  Fall protection plan (required for pitch &gt; 7/12) *
                </Label>
                <Textarea
                  id="fall_protection_plan"
                  value={data.fall_protection_plan ?? ''}
                  onChange={(e) =>
                    setData((d) => ({
                      ...d,
                      fall_protection_plan: e.target.value,
                    }))
                  }
                  placeholder="Describe how the roof will be accessed safely (anchor points, harness, lifelines, etc.)"
                  className="min-h-[80px]"
                />
              </section>
            )}

            {/* Signature */}
            <section className="flex flex-col gap-1.5">
              <Label htmlFor="signed_by">Signed by (full name) *</Label>
              <Input
                id="signed_by"
                value={data.signed_by}
                onChange={(e) =>
                  setData((d) => ({ ...d, signed_by: e.target.value }))
                }
                placeholder="Your name"
              />
            </section>
          </TabsContent>

          {/* ── Upload PDF ───────────────────────────────────────── */}
          <TabsContent value="upload" className="flex flex-col gap-5 pt-4">
            <p className="text-xs text-[var(--ink-3)]">
              Upload a completed signed Risk Assessment PDF or photo of the
              paper form. Use the in-app form for new inspections going
              forward.
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="upload">
                Completed Risk Assessment PDF or photo *
              </Label>
              <FileUploader
                value={
                  data.uploaded_file_url
                    ? [{ uuid: data.uploaded_file_url, file_name: 'Risk assessment' }]
                    : []
                }
                onChange={(files) =>
                  setData((d) => ({ ...d, uploaded_file_url: files[0]?.uuid }))
                }
                uploadFn={async (file) => {
                  try {
                    const uploaded = await uploadFile.mutateAsync({
                      file,
                      use_case: 'INSPECTION_EVIDENCE',
                    })
                    return {
                      uuid: uploaded.uuid,
                      file_name: uploaded.file_name ?? file.name,
                      file_url: uploaded.file_url,
                      mime_type: (uploaded as { mime_type?: string }).mime_type,
                    }
                  } catch (err) {
                    toastApiError(err, 'Failed to upload file.')
                    throw err
                  }
                }}
                accept="application/pdf,image/jpeg,image/png"
                acceptLabel="PDF, JPG, PNG"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="signed_by_upload">Signed by (full name) *</Label>
              <Input
                id="signed_by_upload"
                value={data.signed_by}
                onChange={(e) =>
                  setData((d) => ({ ...d, signed_by: e.target.value }))
                }
                placeholder="Your name"
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || busy}
          >
            {busy ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" />
                Saving...
              </>
            ) : isBeginFlow ? (
              <>
                <ShieldCheck size={14} className="mr-1.5" />
                Save &amp; begin risk assessment
              </>
            ) : (
              <>
                <CheckCircle2 size={14} className="mr-1.5" />
                Save risk assessment
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
